import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ediciones,
  edicionPendiente,
  versionVista,
} from '#components/mobile/novedades/catalogo';
import { useTravesia } from '#components/mobile/travesia/TravesiaContext';
import { usePlan } from '#hooks/usePlan';
import { pedirPuente, registrarUso } from '#uso';

import { HojaEncuesta } from './HojaEncuesta';
import { encuestaQueToca, trasAhoraNo } from './preguntas';
import type { NombreEncuesta } from './preguntas';
import {
  contarApertura,
  guardarRegistro,
  leerRegistro,
  marcarCerrada,
} from './registroEncuesta';

/**
 * Rumbo: ofrece una encuesta de Gavi al abrir la app, si toca (ver
 * `encuestaQueToca`): la de la beta a los 10 días y la del día 40 de la
 * prueba. Nunca durante la primera travesía, ni con otra ventana de una vez
 * abierta, ni sin cuenta en el servidor (no tendría adónde enviarla). El día
 * que salen las novedades de una versión, tampoco: una ventana por día basta.
 */
export function Encuesta({ esperar = false }: { esperar?: boolean }) {
  const { t } = useTranslation();
  const [hayNovedades] = useState(
    () => edicionPendiente(ediciones(t), versionVista()) != null,
  );
  const travesiaActiva = !!useTravesia()?.activa;
  const { plan, cargando, sinServidor } = usePlan();
  const [registro] = useState(() => contarApertura());
  const [abierta, setAbierta] = useState<NombreEncuesta | null>(null);
  // Se decide una vez por apertura (no en cada cambio del plan).
  const decidida = useRef(false);

  useEffect(() => {
    if (
      decidida.current ||
      hayNovedades ||
      esperar ||
      travesiaActiva ||
      sinServidor ||
      cargando
    ) {
      return;
    }
    decidida.current = true;
    const ahora = Date.now();
    // Primero sin preguntar al servidor: casi siempre no toca nada.
    if (!encuestaQueToca({ registro, plan, respondidas: [], ahora })) {
      return;
    }
    pedirPuente<NombreEncuesta[]>('/encuesta/mias')
      .then(respondidas => {
        const toca = encuestaQueToca({ registro, plan, respondidas, ahora });
        if (toca) {
          setAbierta(toca);
          registrarUso('encuesta_ofrecida', { encuesta: toca });
        }
      })
      .catch(() => {
        // Sin red: se ofrecerá otro día.
      });
  }, [
    hayNovedades,
    esperar,
    travesiaActiva,
    sinServidor,
    cargando,
    plan,
    registro,
  ]);

  if (!abierta) {
    return null;
  }

  return (
    <HojaEncuesta
      encuesta={abierta}
      onCerrar={fin => {
        if (fin === 'enviada') {
          marcarCerrada(abierta);
        } else {
          guardarRegistro(trasAhoraNo(leerRegistro(), abierta, Date.now()));
          registrarUso('encuesta_ahora_no', { encuesta: abierta });
        }
        setAbierta(null);
      }}
    />
  );
}
